import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Camera, History, Home, Plus, Settings as SettingsIcon, TrendingUp, Users, Command, ArrowRight, Wallet } from "lucide-react";
import { cn } from "@/lib/utils";

type Action = {
  id: string;
  group: "Navigation" | "Actions";
  label: string;
  keywords?: string;
  icon?: React.ComponentType<{ className?: string }>;
  run: () => void;
};

export function NeonCommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [active, setActive] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const actions = React.useMemo<Action[]>(
    () => [
      { id: "home", group: "Navigation", label: "Dashboard", keywords: "home", icon: Home, run: () => navigate("/") },
      { id: "sessions", group: "Navigation", label: "Sessions", keywords: "history list", icon: History, run: () => navigate("/sessions") },
      { id: "players", group: "Navigation", label: "Players", keywords: "roster", icon: Users, run: () => navigate("/players") },
      { id: "expenses", group: "Navigation", label: "Expenses", keywords: "wallet receipts", icon: Wallet, run: () => navigate("/expenses") },
      { id: "stats", group: "Navigation", label: "Statistics", keywords: "stats leaderboard", icon: TrendingUp, run: () => navigate("/statistics") },
      { id: "settings", group: "Navigation", label: "Settings", keywords: "prefs config", icon: SettingsIcon, run: () => navigate("/settings") },
      { id: "new", group: "Actions", label: "New Session", keywords: "create start session", icon: Plus, run: () => navigate("/session/new") },
      { id: "import", group: "Actions", label: "Import Session", keywords: "photo ocr scan", icon: Camera, run: () => navigate("/session/import") },
    ],
    [navigate]
  );

  const filtered = React.useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return actions;
    return actions.filter((a) => `${a.label} ${a.keywords ?? ""}`.toLowerCase().includes(s));
  }, [actions, q]);

  const groups = React.useMemo(() => {
    const map: Record<string, Action[]> = {};
    filtered.forEach((a) => {
      if (!map[a.group]) map[a.group] = [];
      map[a.group].push(a);
    });
    return map;
  }, [filtered]);

  const flat = filtered;

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (!open) return;
      if (e.key === "Escape") setOpen(false);
      if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, flat.length - 1)); }
      if (e.key === "ArrowUp")   { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
      if (e.key === "Enter") {
        e.preventDefault();
        const a = flat[active];
        if (!a) return;
        a.run();
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [active, flat, open]);

  React.useEffect(() => {
    if (!open) return;
    setQ("");
    setActive(0);
    const t = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(t);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center pt-[15vh] px-4 bg-neon-bg/40 backdrop-blur-md"
      onClick={() => setOpen(false)}
    >
      <div
        className="glass-card w-full max-w-[560px] overflow-hidden shadow-2xl border-white/10 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 p-4 border-b border-white/5 bg-white/5">
          <Command className="w-5 h-5 text-neon-purple" />
          <input
            ref={inputRef}
            className="flex-1 bg-transparent border-none outline-none text-white text-base placeholder:text-neon-muted font-medium"
            placeholder="Type a command or search..."
            value={q}
            onChange={(e) => { setQ(e.target.value); setActive(0); }}
          />
          <div className="flex items-center gap-1.5">
            <kbd className="px-1.5 py-0.5 rounded-md bg-white/10 border border-white/10 text-[10px] font-black text-neon-muted">ESC</kbd>
          </div>
        </div>

        <div className="max-h-[400px] overflow-y-auto p-2 custom-scrollbar">
          {filtered.length === 0 ? (
            <div className="p-12 text-center flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center">
                <Search className="w-6 h-6 text-neon-muted opacity-20" />
              </div>
              <div className="text-xs font-black text-neon-muted uppercase tracking-widest text-center">No matching commands</div>
            </div>
          ) : (
            Object.entries(groups).map(([group, items]) => (
              <div key={group} className="mb-2">
                <div className="px-3 py-2 text-[10px] font-black text-neon-muted uppercase tracking-[0.2em]">
                  {group}
                </div>
                {items.map((a) => {
                  const idx = flat.indexOf(a);
                  const isActive = idx === active;
                  const Icon = a.icon;
                  return (
                    <button
                      key={a.id}
                      type="button"
                      className={cn(
                        "w-full flex items-center justify-between px-3 py-3 rounded-xl transition-all duration-200 group",
                        isActive 
                          ? "bg-neon-purple/20 border border-neon-purple/40 shadow-[0_0_20px_hsl(var(--neon-purple)/0.15)]" 
                          : "bg-transparent border border-transparent hover:bg-white/5"
                      )}
                      onMouseEnter={() => setActive(idx)}
                      onClick={() => { a.run(); setOpen(false); }}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
                          isActive ? "bg-neon-purple text-white shadow-lg shadow-neon-purple/30" : "bg-white/5 text-neon-muted"
                        )}>
                          {Icon && <Icon className="w-4 h-4" />}
                        </div>
                        <span className={cn(
                          "text-sm font-bold transition-colors uppercase tracking-wider",
                          isActive ? "text-white" : "text-neon-muted group-hover:text-white"
                        )}>{a.label}</span>
                      </div>
                      {isActive && (
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-black text-neon-purple tracking-widest uppercase">Select</span>
                          <ArrowRight className="w-4 h-4 text-neon-purple" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="p-3 border-t border-white/5 bg-white/5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-[9px] font-black text-neon-muted">↑↓</kbd>
              <span className="text-[9px] font-black text-neon-muted uppercase tracking-tighter">Navigate</span>
            </div>
            <div className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-[9px] font-black text-neon-muted">ENTER</kbd>
              <span className="text-[9px] font-black text-neon-muted uppercase tracking-tighter">Run</span>
            </div>
          </div>
          <div className="text-[9px] font-black text-neon-purple uppercase tracking-widest animate-pulse">
            SSC Command Center
          </div>
        </div>
      </div>
    </div>
  );
}
